import {
  getMarginRequirement,
  getSecurityHoldingsValue,
  Holding,
  makeDecimal,
} from "@frec-js/common";
import Decimal from "decimal.js";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getMarginCallValues } from "../../utils";
import { useIsAccountInGoodShape } from "./useIsAccountInGoodShape";

export interface ForecasterProps {
  loading: boolean;
  loanAmount: number | Decimal;
  allHoldings: Holding[];
  allCash: number | Decimal;
  portfolioChangePercent: number;
  onChangePortfolioChangePercent: (n: number) => void;
  resetPortfolioChangePercent: () => void;
  additionalTransferAmount?: number;
  onAdditionalTransferAmountChange?: (n: number) => void;
  additionalBorrowAmount?: number;
  onAdditionalBorrowAmountChange?: (n: number) => void;
  useRedSlider?: boolean;
  skipHistoricalSimulation?: boolean;
}

// TODO: clean this up
export enum ForecasterContext {
  // Used for portfolio line of credit page with loan
  PortfolioLineOfCredit = "PortfolioLineOfCredit",
  // Used for portfolio line of credit page without loan
  PortfolioLineOfCreditBorrowView = "PortfolioLineOfCreditBorrowView",
  StockTransfer = "StockTransfer",
  BorrowRisk = "BorrowRisk",
  DebugPage = "DebugPage",
}

export const useForecasterProps = (args: {
  allCash: Decimal | number;
  allHoldings: Holding[];
  loanAmount: Decimal | number;
  context: ForecasterContext;
  skipHistoricalSimulation?: boolean;
}): ForecasterProps => {
  const {
    allCash,
    allHoldings: holdingsInput,
    loanAmount: loanAmountInput,
    context,
    skipHistoricalSimulation,
  } = args;

  const [portfolioChangePercent, setPortfolioChangePercent] = useState(0);
  const [additionalTransferAmount, setAdditionalTransferAmount] = useState(0);
  const [additionalBorrowAmount, setAdditionalBorrowAmount] = useState<
    number | undefined
  >(undefined);

  const debouncedSetAdditionalBorrowAmount = useCallback(
    debounce((x: number) => {
      setAdditionalBorrowAmount(x);
    }, 200),
    [setAdditionalBorrowAmount],
  );

  // We debounce `additionalTransferAmount` because it triggers a lot of computations
  // when we rebuild holdings.
  const debouncedSetAdditionalTransferAmount = useCallback(
    debounce((x: number) => {
      setAdditionalTransferAmount(x);
    }, 200),
    [setAdditionalTransferAmount],
  );

  /**
   * Rebuild holdings considering additional transfer amounts, here we assume
   * that each holding increases proportionally.
   *
   * TODO: (WEB-324) changes to `holdings` like this can create a sluggish experience, there may
   * be a better way.
   */
  const holdings = useMemo(() => {
    if (additionalTransferAmount) {
      const additionPerHolding =
        additionalTransferAmount / holdingsInput.length;
      return holdingsInput.map((h) => {
        const additionalQuantity = additionPerHolding / h.price.toNumber();
        return { ...h, quantity: h.quantity.add(additionalQuantity) };
      });
    }
    return holdingsInput;
  }, [additionalTransferAmount, holdingsInput]);

  const showAdditionalBorrow =
    context === ForecasterContext.PortfolioLineOfCreditBorrowView;

  /**
   * Loan amount is similarly adjusted.
   */
  const loanAmount = useMemo(
    () =>
      makeDecimal(loanAmountInput).add(
        showAdditionalBorrow ? (additionalBorrowAmount ?? 0) : 0,
      ),
    [additionalBorrowAmount, loanAmountInput, showAdditionalBorrow],
  );

  // Keep marginCallValues updated
  const marginCallValues = useMemo(
    () =>
      getMarginCallValues({ allCash, allHoldings: holdingsInput, loanAmount }),
    [allCash, holdingsInput, loanAmount],
  );

  const { data: isAccountInGoodShape, loading: accountShapeLoading } =
    useIsAccountInGoodShape(
      allCash,
      loanAmount,
      holdings,
      marginCallValues.portfolioPercentDropAtMarginCall,
      skipHistoricalSimulation,
    );

  const result = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const safeDrop = marginCallValues.portfolioPercentDropAtMarginCall
      ? marginCallValues.portfolioPercentDropAtMarginCall.floor().toNumber()
      : 100;

    const hasMarginCall =
      marginCallValues.portfolioPercentDropAtMarginCall.lte(0);

    // By default forecaster will start at %-drop to material loan repayment, unless
    // the account is already in margin call, at which point the "drop" will be a negative
    // value, so we default to 0.
    let initialPercent = hasMarginCall
      ? 0
      : (marginCallValues.portfolioPercentDropToMaterialLoanRepayment
          ?.neg()
          .toNumber() ?? 0);

    switch (context) {
      case ForecasterContext.PortfolioLineOfCredit:
        // On the home page we default to 0% if in good shape.
        initialPercent = isAccountInGoodShape ? 0 : initialPercent;
        break;
      case ForecasterContext.PortfolioLineOfCreditBorrowView:
        initialPercent = makeDecimal(loanAmountInput).eq(0) ? 0 : -safeDrop;
        break;
    }

    // used for the `onClose` externally
    const resetPortfolioChangePercent = () =>
      setPortfolioChangePercent(initialPercent);

    const showAdditionalTransfer =
      context === ForecasterContext.BorrowRisk ||
      context === ForecasterContext.DebugPage;

    // We only use the red "margin call" slider in special cases
    const useRedSlider =
      context === ForecasterContext.PortfolioLineOfCredit &&
      !isAccountInGoodShape;

    return {
      loading: accountShapeLoading,
      loanAmount,
      allHoldings: holdings,
      allCash,
      portfolioChangePercent,
      onChangePortfolioChangePercent: setPortfolioChangePercent,
      resetPortfolioChangePercent,
      additionalTransferAmount,
      onAdditionalTransferAmountChange: showAdditionalTransfer
        ? debouncedSetAdditionalTransferAmount
        : undefined,
      additionalBorrowAmount,
      onAdditionalBorrowAmountChange: showAdditionalBorrow
        ? debouncedSetAdditionalBorrowAmount
        : undefined,
      useRedSlider,
    };
  }, [
    accountShapeLoading,
    additionalBorrowAmount,
    additionalTransferAmount,
    context,
    debouncedSetAdditionalBorrowAmount,
    debouncedSetAdditionalTransferAmount,
    allCash,
    holdings,
    isAccountInGoodShape,
    loanAmount,
    loanAmountInput,
    marginCallValues.portfolioPercentDropAtMarginCall,
    marginCallValues.portfolioPercentDropToMaterialLoanRepayment,
    portfolioChangePercent,
    showAdditionalBorrow,
  ]);

  // If we're showing the additional borrow amount, we want to start at the
  // amount that would not cause a margin call even if the portfolio drops 50%.
  useEffect(() => {
    if (
      additionalBorrowAmount !== undefined &&
      !isNaN(additionalBorrowAmount)
    ) {
      return;
    }

    const marginRequirement = getMarginRequirement(holdings);
    const holdingValue = getSecurityHoldingsValue(holdings);

    // Since we want 50% drop as default for forecaster
    // HoldingValue * 0.50 * LTV = existingLoan + newLoan - cash
    // newLoan = HoldingValue * 0.50 * LTV + cash - existingLoan
    const borrowAmountNew = holdingValue
      .mul(0.49)
      .mul(1 - marginRequirement.toNumber())
      .add(allCash)
      .minus(loanAmountInput);

    // If The borrow amount needed for a safe 50% portfolio drop is less than
    // the current borrow amount, we don't want to change the borrow amount to
    // a negative number.
    setAdditionalBorrowAmount(
      Math.max(0, Math.ceil(borrowAmountNew.toNumber())),
    );
    // Disable exhaustive deps because we only want to run this once as a default
  }, [allCash, holdings]);

  // If and only if holdings/loan have changed do we want to reset the slider.
  // Since holdings change when prices change, we sum the quantities.
  const totalHoldingsQuantity = holdings.reduce(
    (acc, h) => acc + h.quantity.toNumber(),
    0,
  );
  useEffect(
    () => result.resetPortfolioChangePercent(),
    [totalHoldingsQuantity, loanAmount.toNumber()],
  );

  return result;
};
